import i18n from '@i18next/i18n';

export interface Options {
    autoClose?: boolean;
    type?: 'info' | 'success' | 'error' | 'loading';
    message?: string;
    duration?: number;
}

// 默认showSnackbar配置
const defaultOptions: Options = {
    autoClose: false,
    type: 'loading',
    message: i18n.t('正在加载中...'),
};
// 默认自动关闭的延时
const defaultDuration = 2000;

/**
 * 将Snackbar组件注入，生成模块内唯一的Snackbar
 * 注：采用注入组件的方式是因为Snackbar组件分pc和m，
 *    目前没有合适的项目目录来做继承多态，所以采用动态注入
 * 打开、更新、关闭都操纵同一个Snackbar实例，实现互斥效果
 * 可用于 loading开始 -> 成功tip|失败tip|关闭loading
 */
export default class SingleSnackbar {
    private Snackbar: any;
    private instance: any;
    private isLoadingNow: boolean;
    private currentOptions?: Options;
    private closeTimer?: number;
    private readonly defaultOptions: Options;

    /**
     * 将Snackbar组件传入构造函数以初始化
     * 注：因为PC/M的Snackbar不一样
     * @param Snackbar
     */
    public constructor({ Snackbar }) {
        this.Snackbar = Snackbar;
        this.defaultOptions = defaultOptions;
        this.isLoadingNow = false;
    }

    /**
     * 显示Snackbar(未打开便创建，已有则更新)
     * @param options 可依据dui配置覆盖默认配置
     */
    public show(options: Options): void {
        const targetOptions = { ...this.defaultOptions, ...options };
        clearTimeout(this.closeTimer);
        if (!this.isLoadingNow) {
            // 当前无提示条，则创建
            this.instance = this.Snackbar.show(targetOptions);
        } else {
            // 当前有提示条，则更新
            this.instance?.update(targetOptions);
            // 如果是从不自动关闭变成自动关闭，因为sc组件不支持，所以需要模拟
            this.mockAutoClose(targetOptions, this.currentOptions);
        }
        /**
         * Snackbar组件在autoClose为false的时候才支持手动控制
         * 所以在autoClose为false时才用isLoadingNow做标记来做更新或关闭控制
         */
        if (!targetOptions.autoClose) {
            this.isLoadingNow = true;
        }
        this.currentOptions = targetOptions;
    }

    /**
     * 关闭Snackbar 清空标记
     */
    public close(): void {
        if (this.isLoadingNow && this.instance) {
            this.instance.close();
            this.instance = null;
        }
        this.isLoadingNow = false;
        this.currentOptions = undefined;
    }

    /**
     * Snackbar组件的update不支持autoClose从false变为true，所以用定时器模拟延时关闭效果
     * @param currentOptions 当前Options
     * @param targetOptions 目标Options
     */
    private mockAutoClose(targetOptions: Options, currentOptions?: Options): void {
        clearTimeout(this.closeTimer);
        if (targetOptions.autoClose && (currentOptions?.autoClose === false)) {
            this.closeTimer = window.setTimeout(() => {
                this.close();
            }, targetOptions.duration || defaultDuration);
        }
    }
}
